import { useState, FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/AuthContext";
import { callableErrorCode } from "../lib/invite";
import { requestCodeErrorKey, verifyCodeErrorKey } from "../lib/loginCode";

type LoginStep = "email" | "code";

export default function LoginPage() {
  const { t } = useTranslation();
  const [step, setStep] = useState<LoginStep>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { requestLoginCode, verifyLoginCode, signInWithGoogle } = useAuth();

  const handleGoogleSignIn = async () => {
    setError("");
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      if (err instanceof Error && err.message !== "Sign-in was cancelled.") {
        setError(err.message || "Failed to sign in with Google.");
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleRequestCode = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await requestLoginCode(email);
      setStep("code");
    } catch (err) {
      setError(t(requestCodeErrorKey(err)));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await verifyLoginCode(email, code);
      // Auth state change will redirect automatically
    } catch (err) {
      setError(t(verifyCodeErrorKey(err)));
      if (callableErrorCode(err) === "resource-exhausted") {
        setStep("email");
        setCode("");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError("");
    setLoading(true);
    try {
      await requestLoginCode(email);
      setCode("");
    } catch (err) {
      setError(t(requestCodeErrorKey(err, true)));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep("email");
    setCode("");
    setError("");
  };

  const handleDevLogin = async () => {
    setError("");
    setLoading(true);
    const devEmail = "dev@naramachine.ai";
    const devPassword = "dev123456";
    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
    const emulatorUrl = "http://127.0.0.1:9099";

    try {
      // Ensure dev user exists in the Auth emulator via admin API
      await fetch(
        `${emulatorUrl}/identitytoolkit.googleapis.com/v1/projects/${projectId}/accounts`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer owner",
          },
          body: JSON.stringify({
            email: devEmail,
            password: devPassword,
            displayName: "Dev User",
            emailVerified: true,
          }),
        }
      );

      // Sign in — blocking functions will detect emulator and skip API calls
      const { signInWithEmailAndPassword } = await import("firebase/auth");
      const { auth } = await import("../lib/firebase");
      await signInWithEmailAndPassword(auth, devEmail, devPassword);
    } catch (err) {
      setError(
        "Dev login failed: " +
          (err instanceof Error ? err.message : "Unknown error")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="text-center text-3xl font-bold text-foreground">
            NaraHub
          </h1>
          <h2 className="mt-6 text-center text-xl text-muted-foreground">
            {step === "email" ? t("login.title") : t("login.verificationTitle")}
          </h2>
          {step === "code" && (
            <p
              className="mt-2 text-center text-sm text-muted-foreground"
              dangerouslySetInnerHTML={{ __html: t("login.codeSent", { email }) }}
            />
          )}
        </div>

        <div className="mt-8 space-y-6">
          {error && (
            <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Dev-only emulator bypass */}
          {import.meta.env.DEV && (
            <>
              <button
                type="button"
                onClick={handleDevLogin}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-2 px-4 border-2 border-dashed border-nara-orange rounded-md text-sm font-medium text-nara-orange bg-nara-orange/10 hover:bg-nara-orange/20 transition-colors disabled:opacity-50"
              >
                Dev Login (dev@naramachine.ai)
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-muted text-muted-foreground">or</span>
                </div>
              </div>
            </>
          )}

          {step === "email" ? (
            <>
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={googleLoading || loading}
                className="w-full flex items-center justify-center gap-3 py-2 px-4 border border-border rounded-md shadow-sm text-sm font-medium text-foreground bg-card hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {googleLoading ? t("login.signingIn") : t("login.googleButton")}
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-muted text-muted-foreground">{t("login.orEmail")}</span>
                </div>
              </div>

              <form onSubmit={handleRequestCode} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground">
                    {t("login.emailLabel")}
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t("login.emailPlaceholder")}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? t("login.sending") : t("login.sendCode")}
                </button>
              </form>
            </>
          ) : (
            <form onSubmit={handleVerifyCode} className="space-y-6">
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-foreground">
                  {t("login.codeLabel")}
                </label>
                <input
                  id="code"
                  name="code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="mt-1 block w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest"
                  placeholder="000000"
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t("login.verifying") : t("login.verify")}
              </button>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={loading}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  {t("login.back")}
                </button>
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={loading}
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  {t("login.resendCode")}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
